import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, NEIGHBORHOOD_OPTIONS, todayISO, plural } from "../lib/constants";
import { EventCard } from "../components/EventCard";
import { PopularEvents } from "../components/PopularEvents";
import { Search, SlidersHorizontal, X, Moon } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0318cc04-21bf-446a-8df6-374fe4adb994/images/2bfccdfeb891e8713392cf090d12e4529049d4b48fbeb00990f226fc32f6a7a6.png";

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

export default function Home() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [favIds, setFavIds] = useState(new Set());

  const category = params.get("category") || "";
  const neighborhood = params.get("neighborhood") || "";
  const date = params.get("date") || "";
  const today = todayISO();

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (category) q.category = category;
      if (neighborhood) q.neighborhood = neighborhood;
      if (date) q.date = date;
      if (search) q.search = search;
      const { data } = await api.get("/events", { params: q });
      setEvents(data);
    } finally {
      setLoading(false);
    }
  }, [category, neighborhood, date, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) { setFavIds(new Set()); return; }
    api.get("/favorites").then((r) => setFavIds(new Set(r.data.map((e) => e.id)))).catch(() => {});
  }, [user]);

  const toggleFav = async (id) => {
    if (!user) { toast.error("Accedi per salvare i preferiti"); return; }
    const next = new Set(favIds);
    try {
      if (next.has(id)) { await api.delete(`/favorites/${id}`); next.delete(id); }
      else { await api.post(`/favorites/${id}`); next.add(id); toast.success("Aggiunto ai preferiti"); }
      setFavIds(next);
    } catch { toast.error("Operazione fallita"); }
  };

  const clearFilters = () => setParams(new URLSearchParams());
  const hasFilters = category || neighborhood || date || search;

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[85vh] min-h-[520px] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Vita notturna a Milano" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-wyd-bg via-wyd-bg/70 to-wyd-bg/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-wyd-bg/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 pb-20 w-full">
          <div className="overline text-wyd-accent mb-4 fade-up">Le tue serate a Milano</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl fade-up" style={{ animationDelay: "80ms" }}>
            Scopri dove si esce <span className="text-gradient">stasera</span>
          </h1>
          <p className="mt-6 text-lg text-wyd-muted max-w-xl fade-up" style={{ animationDelay: "160ms" }}>
            Trova la tua serata, ovunque tu sia.
          </p>
          <div className="mt-8 max-w-lg relative fade-up" style={{ animationDelay: "240ms" }}>
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-wyd-muted" />
            <input
              data-testid="hero-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca eventi, locali, promo…"
              className="w-full pl-12 pr-4 py-4 rounded-full bg-wyd-surface/90 border border-white/10 text-white placeholder-wyd-muted focus:ring-2 focus:ring-wyd-accent focus:outline-none"
            />
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="max-w-7xl mx-auto px-5 -mt-8 relative z-10">
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3 text-wyd-muted text-sm">
            <SlidersHorizontal size={16} /> <span className="overline">Filtri</span>
            {hasFilters && (
              <button data-testid="clear-filters" onClick={clearFilters} className="ml-auto flex items-center gap-1 text-wyd-accent hover:underline text-xs">
                <X size={13} /> Azzera
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            <button data-testid="filter-tonight" onClick={() => setParam("date", date === today ? "" : today)}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                date === today ? "bg-wyd-accent text-wyd-bg" : "bg-wyd-accent/10 text-wyd-accent border border-wyd-accent/40 hover:bg-wyd-accent/20"
              }`}>
              <Moon size={14} /> Stasera
            </button>
            <span className="shrink-0 w-px bg-white/10 mx-1" />
            <Pill active={!category} onClick={() => setParam("category", "")} testid="cat-all">Tutte</Pill>
            {CATEGORIES.map((c) => (
              <Pill key={c} active={category === c} onClick={() => setParam("category", c)} testid={`cat-${c}`}>{c}</Pill>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <select data-testid="filter-neighborhood" value={neighborhood} onChange={(e) => setParam("neighborhood", e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-xl bg-wyd-bg border border-white/10 text-white text-sm focus:ring-2 focus:ring-wyd-accent focus:outline-none">
              <option value="">Tutti i quartieri</option>
              {NEIGHBORHOOD_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input data-testid="filter-date" type="date" value={date} onChange={(e) => setParam("date", e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-xl bg-wyd-bg border border-white/10 text-white text-sm focus:ring-2 focus:ring-wyd-accent focus:outline-none" />
          </div>
        </div>
      </section>

      {/* GRID */}
      {!hasFilters && <PopularEvents favIds={favIds} onToggleFavorite={toggleFav} />}
      <section className="max-w-7xl mx-auto px-5 pt-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-2xl md:text-3xl">Prossimi eventi</h2>
          <span className="text-sm text-wyd-muted" data-testid="events-count">{plural(events.length, "risultato", "risultati")}</span>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {SKELETON_KEYS.map((k) => (
              <div key={k} className="h-80 rounded-2xl bg-wyd-surface border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div data-testid="no-events" className="text-center py-24 text-wyd-muted">
            <p className="text-lg">Nessun evento trovato.</p>
            <p className="text-sm mt-2">Prova a cambiare i filtri o torna più tardi.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {events.map((e, i) => (
              <EventCard key={e.id} event={e} index={i} isFavorite={favIds.has(e.id)} onToggleFavorite={toggleFav} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const Pill = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
      active ? "bg-wyd-accent text-wyd-bg border-wyd-accent" : "bg-wyd-surface text-wyd-muted border-white/10 hover:text-white"
    }`}
  >
    {children}
  </button>
);
